import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { searchUsersForRfid, type AdminUserSearchRow } from '@/lib/api';
import formStyles from './Form.module.css';
import styles from './UserTypeahead.module.css';

/**
 * Shared user-search typeahead — extracted from the inline picker
 * that originally lived in RfidCardFormModal so the third-party-
 * charger-access ticket's guest pass list (commit 10) can reuse it.
 *
 * Two consumers as of this commit:
 *   - RfidCardFormModal: single-select (maxSelections=1) for the
 *     driver picker on RFID card create/edit.
 *   - ChargerDetailPage Guest Access section: multi-select up to
 *     100 users for the guest pass list.
 *
 * Tenant scoping: hits the existing `searchUsersForRfid` helper
 * which calls `GET /admin/users/search` — same-tenant only for
 * tenant_admin, all-tenant for super_admin (server-side default,
 * unchanged by this commit). Per the locked decision in the
 * third-party-access discovery: tenant_admins can only add users
 * from their own tenant to a guest pass list.
 *
 * UX:
 *   - Selected users render as removable chips above the input.
 *   - Search input is debounced 220ms (matches the previous
 *     RfidCardFormModal cadence).
 *   - Result list panel shows username/email + name in two lines.
 *   - Clicking a result adds it to the selection and clears the
 *     query.
 *   - At maxSelections, the search input is disabled and a
 *     "{n}/{max} users" counter renders. Removing a chip drops the
 *     count and re-enables the input automatically.
 *   - excludeUserIds suppresses already-selected users from the
 *     suggestion panel even if they match the query (so the user
 *     can't add the same id twice).
 */

export interface UserTypeaheadProps {
  /** ObjectId strings of currently-selected users. */
  selectedUserIds: string[];
  /** Called with the next selection. Caller persists. */
  onChange: (ids: string[]) => void;
  /** Cap on the selection size. Undefined = unlimited. */
  maxSelections?: number;
  placeholder?: string;
  disabled?: boolean;
  /** User ids to suppress from suggestions (typically === selectedUserIds;
   *  exposed as a separate prop so the caller can also exclude e.g. the
   *  charger's tenant-admin themselves if that ever becomes a rule). */
  excludeUserIds?: string[];
  /** Optional accessibility label for the input. */
  ariaLabel?: string;
}

/** Lightweight cache of fetched user details so chips can render
 *  name/email without re-hitting the API after the user picks. The
 *  caller's `selectedUserIds` is the source of truth; this is purely
 *  display-side hydration that pre-populates from search results
 *  AND from the resolved chips of an initial mount. */
type UserCache = Map<string, AdminUserSearchRow>;

export function UserTypeahead({
  selectedUserIds,
  onChange,
  maxSelections,
  placeholder = 'Search by email, username, or name…',
  disabled = false,
  excludeUserIds,
  ariaLabel,
}: UserTypeaheadProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AdminUserSearchRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [userCache, setUserCache] = useState<UserCache>(() => new Map());
  const timer = useRef<number | null>(null);
  // Per-mount random field name + the attributes on the input below defeat
  // browser / password-manager autofill on this user-search box.
  // autoComplete="off" alone is unreliable: Chrome and the password managers
  // ignore it on an email-ish field and surface the operator's OWN saved
  // emails -- a privacy leak when adding a guest to a charger. A name the
  // browser has never seen, plus the 1Password / LastPass ignore hints, stop
  // the suggestion dropdown entirely.
  const [autofillName] = useState(
    () => `evi-user-search-${Math.random().toString(36).slice(2, 10)}`,
  );

  const atCapacity = typeof maxSelections === 'number'
    && selectedUserIds.length >= maxSelections;
  const inputDisabled = disabled || atCapacity;

  // Suppress IDs the caller asks to exclude (default: the current
  // selection — can't pick the same user twice).
  const exclusionSet = useMemo(() => {
    const set = new Set<string>();
    for (const id of selectedUserIds) set.add(id);
    if (excludeUserIds) for (const id of excludeUserIds) set.add(id);
    return set;
  }, [selectedUserIds, excludeUserIds]);

  // Debounced search. Skips when query empty OR at capacity (input
  // is disabled in that state, but defense-in-depth in case a stale
  // query string survived).
  useEffect(() => {
    if (!query.trim() || atCapacity) {
      setResults([]);
      return;
    }
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      setSearching(true);
      void searchUsersForRfid(query)
        .then((rows) => {
          setResults(rows);
          // Pre-populate display cache with everything the search
          // surfaced — covers the common case where the user picks
          // a result, then the chip can render name/email immediately
          // without a follow-up fetch.
          setUserCache((prev) => {
            const next = new Map(prev);
            for (const r of rows) next.set(r._id, r);
            return next;
          });
        })
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 220);
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [query, atCapacity]);

  const visibleResults = useMemo(
    () => results.filter((u) => !exclusionSet.has(u._id)),
    [results, exclusionSet],
  );

  const handlePick = (user: AdminUserSearchRow): void => {
    if (exclusionSet.has(user._id)) return;
    if (atCapacity) return;
    setUserCache((prev) => {
      const next = new Map(prev);
      next.set(user._id, user);
      return next;
    });
    onChange([...selectedUserIds, user._id]);
    setQuery('');
    setOpen(false);
    setResults([]);
  };

  const handleRemove = (id: string): void => {
    onChange(selectedUserIds.filter((x) => x !== id));
  };

  const renderChip = (id: string): React.ReactNode => {
    const cached = userCache.get(id);
    const label = cached
      ? (cached.username ?? cached.email)
      : id; // Falls back to raw id when chip mounts without prior
            // search-cache population (e.g. a guest pass list seeded
            // via the API). Visual ugly but functional; resolves to
            // a label as soon as the search surfaces the user.
    return (
      <span key={id} className={styles.chip}>
        <span className={styles.chipText}>{label}</span>
        <button
          type="button"
          className={styles.chipClear}
          onClick={() => handleRemove(id)}
          disabled={disabled}
          aria-label={`Remove ${label}`}
        >
          <X size={12} />
        </button>
      </span>
    );
  };

  return (
    <div className={styles.wrap}>
      {selectedUserIds.length > 0 && (
        <div className={styles.chipRow}>
          {selectedUserIds.map(renderChip)}
        </div>
      )}

      {typeof maxSelections === 'number' && (
        <div className={styles.counterRow}>
          <span className={styles.counter}>
            {selectedUserIds.length} / {maxSelections} users
          </span>
        </div>
      )}

      <div className={styles.pickerWrap}>
        <input
          className={formStyles.input}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={atCapacity ? `Maximum ${maxSelections} reached` : placeholder}
          disabled={inputDisabled}
          name={autofillName}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          data-1p-ignore="true"
          data-lpignore="true"
          aria-label={ariaLabel ?? 'Search users'}
        />
        {open && !inputDisabled && (query.trim() || searching) && (
          <div className={styles.resultsPanel}>
            {searching && (
              <div className={styles.resultEmpty}>
                <Loader2 size={12} className={styles.spin} /> Searching…
              </div>
            )}
            {!searching && visibleResults.length === 0 && query.trim() && (
              <div className={styles.resultEmpty}>No matches.</div>
            )}
            {!searching && visibleResults.map((u) => (
              <button
                key={u._id}
                type="button"
                className={styles.resultRow}
                onClick={() => handlePick(u)}
              >
                <span className={styles.resultPrimary}>
                  {u.username ?? u.email}
                </span>
                <span className={styles.resultSecondary}>
                  {u.email}{u.name ? ` · ${u.name}` : ''}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
